import type {
  UserGroup,
  GroupRole,
  UserGroupRole,
  CreateUserGroupRequest,
  UpdateUserGroupRequest,
  SortUserGroupsRequest,
  SortRolesRequest,
  UpdateGroupRoleStatusRequest,
  UpdateGroupRolesRequest,
} from '@/types/userGroups.types';
import { ApiClient } from '@/config/axios.config';

/**
 * UserGroupService - handles all API calls related to user groups
 */
export class UserGroupService {
  /**
   * Get all user groups
   * @param isMinimal - If true, returns minimal data
   */
  static async getUserGroups(isMinimal = false): Promise<UserGroup[]> {
    const minimal = isMinimal ? '?minimal=true' : '';
    const userGroups = await ApiClient.get<UserGroup[]>(`/usergroups${minimal}`);
    // Sort by sort order as done in Angular version
    return userGroups.sort((a, b) => a.sort - b.sort);
  }

  /**
   * Create a new user group
   */
  static async createUserGroup(data: CreateUserGroupRequest): Promise<UserGroup> {
    return ApiClient.post<UserGroup>('/usergroups', data);
  }

  /**
   * Update an existing user group
   */
  static async updateUserGroup(
    userGroupId: number,
    data: UpdateUserGroupRequest
  ): Promise<UserGroup> {
    return ApiClient.put<UserGroup>(`/usergroups/${userGroupId}`, data);
  }

  /**
   * Delete a user group
   */
  static async deleteUserGroup(userGroupId: number): Promise<void> {
    await ApiClient.delete<void>(`/usergroups/${userGroupId}`);
  }

  /**
   * Sort user groups by providing an array of user group IDs in the desired order
   */
  static async sortUserGroups(data: SortUserGroupsRequest): Promise<void> {
    await ApiClient.put<void>('/usergroups/sort', data);
  }

  /**
   * Get all available roles
   */
  static async getRoles(): Promise<GroupRole[]> {
    const roles = await ApiClient.get<GroupRole[]>('/usergroups/roles');
    // Sort by sort order as done in Angular version
    return roles.sort((a: GroupRole, b: GroupRole) => (a.sort || 0) - (b.sort || 0));
  }

  /**
   * Sort roles by providing an array of role IDs in the desired order
   */
  static async sortRoles(data: SortRolesRequest): Promise<void> {
    await ApiClient.put<void>('/usergroups/roles/sort', data);
  }

  /**
   * Get group roles by role code
   */
  static async getGroupRolesByCode(code: string): Promise<UserGroupRole[]> {
    return ApiClient.get<UserGroupRole[]>(`/usergroups/roles?rolecode=${code}`);
  }

  /**
   * Update group roles for a specific user group
   */
  static async updateGroupRoles(userGroupId: number, data: UpdateGroupRolesRequest): Promise<void> {
    await ApiClient.put<void>(`/usergroups/${userGroupId}/advance`, data);
  }

  /**
   * Delete a user group role
   */
  static async deleteUserGroupRole(roleId: number): Promise<void> {
    await ApiClient.delete<void>(`/usergroups/roles/${roleId}`);
  }

  /**
   * Update the status of a group role
   */
  static async updateGroupRoleStatus(
    roleCode: string,
    userGroupId: number,
    isActive: boolean
  ): Promise<void> {
    const data: UpdateGroupRoleStatusRequest = {
      roleCode,
      isActive,
    };

    await ApiClient.put<void>(`/usergroups/${userGroupId}/status`, data);
  }
}
